import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CodeIcon from '@mui/icons-material/Code';
import PushPinIcon from '@mui/icons-material/PushPin';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import useVoyagerStore from '../../stores/useVoyagerStore';
import ReadingNote from './ReadingNote';
import { ROWS_PER_PAGE_OPTIONS } from '../../constants';
import SqlPreview from '../SqlPreview';
import ResultErrorCard from './ResultErrorCard';

import QueryExecuteTracker from './QueryExecuteTracker';
import { qbResultTab, qbResultsDock, qbResultsTitle } from '../VoyagerPanes/queryBuilderPresentation';

const RESULTS_MIN_HEIGHT = 42;
const RESULTS_DEFAULT_HEIGHT = 300;
const RESULTS_MAX_HEIGHT = 600;

const BottomResults: React.FC = () => {
  const queryResults = useVoyagerStore((s) => s.queryResults);
  const querySQL = useVoyagerStore((s) => s.querySQL);
  const error = useVoyagerStore((s) => s.error);
  const pinnedResults = useVoyagerStore((s) => s.pinnedResults);
  const activeResultTab = useVoyagerStore((s) => s.activeResultTab);
  const pinCurrentResults = useVoyagerStore((s) => s.pinCurrentResults);
  const unpinResult = useVoyagerStore((s) => s.unpinResult);
  const renamePin = useVoyagerStore((s) => s.renamePin);
  const setActiveResultTab = useVoyagerStore((s) => s.setActiveResultTab);
  const userSettings = useVoyagerStore((s) => s.userSettings);
  const queryRunning = useVoyagerStore((s) => s.queryPhase !== 'idle');
  const [filterText, setFilterText] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [showSQL, setShowSQL] = React.useState(false);
  const [panelHeight, setPanelHeight] = React.useState(RESULTS_DEFAULT_HEIGHT);
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc' | null>(null);
  const [editingTabId, setEditingTabId] = React.useState<string | null>(null);
  const [editingTabLabel, setEditingTabLabel] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const isDragging = React.useRef(false);
  const startY = React.useRef(0);
  const startHeight = React.useRef(0);

  const activePin = pinnedResults.find((p) => p.id === activeResultTab);
  const displayResults = activePin ? activePin.queryResults : queryResults;
  const displaySQL = activePin ? activePin.querySQL : querySQL;

  const heightBeforeCollapse = React.useRef(RESULTS_DEFAULT_HEIGHT);

  // Listen for dx:toggle-sql custom event (keyboard shortcut)
  React.useEffect(() => {
    const handler = () => setShowSQL((prev) => !prev);
    document.addEventListener('dx:toggle-sql', handler);
    return () => document.removeEventListener('dx:toggle-sql', handler);
  }, []);

  const handleDragStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    startY.current = e.clientY;
    startHeight.current = panelHeight;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const handleDragMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = startY.current - ev.clientY;
      const newHeight = Math.min(RESULTS_MAX_HEIGHT, Math.max(RESULTS_MIN_HEIGHT + 40, startHeight.current + delta));
      setPanelHeight(newHeight);
      if (collapsed) setCollapsed(false);
    };

    const handleDragEnd = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, [panelHeight, collapsed]);

  const toggleCollapse = () => {
    if (collapsed) {
      setPanelHeight(heightBeforeCollapse.current);
      setCollapsed(false);
    } else {
      heightBeforeCollapse.current = panelHeight;
      setCollapsed(true);
    }
  };

  const columns = displayResults?.columns ?? [];
  const rows = displayResults?.rows ?? [];

  const filteredRows = filterText.trim()
    ? rows.filter((row) => {
        const needle = filterText.toLowerCase();
        return columns.some((col) => {
          const cell = (row as any)[col];
          if (cell === null || cell === undefined) return 'null'.includes(needle);
          return String(cell).toLowerCase().includes(needle);
        });
      })
    : rows;

  const handleSortClick = (col: string) => {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const av = (a as any)[sortColumn];
      const bv = (b as any)[sortColumn];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const an = Number(av);
      const bn = Number(bv);
      if (!isNaN(an) && !isNaN(bn)) {
        return sortDirection === 'asc' ? an - bn : bn - an;
      }
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredRows, sortColumn, sortDirection]);

  React.useEffect(() => { setCurrentPage(0); }, [filterText, sortColumn, sortDirection, displayResults]);

  const totalFilteredRows = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredRows / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages - 1);
  const paginatedRows = sortedRows.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);

  // The Stroma dock also opens while the first Run is in flight, to carry its tracker.
  if (!displayResults && pinnedResults.length === 0 && !error && !queryRunning) return null;
  const trackerOnly = !displayResults && pinnedResults.length === 0 && !error;

  const rowsToCsv = (data: Record<string, any>[]) => {
    const escape = (v: any) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const header = columns.map(escape).join(',');
    const body = data.map((row) =>
      columns.map((col) => escape((row as any)[col])).join(',')
    );
    return [header, ...body].join('\n');
  };

  const downloadFile = (content: string, filename: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyClipboard = async () => {
    const csv = rowsToCsv(sortedRows);
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = csv;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExportCsv = () => {
    const csv = rowsToCsv(sortedRows);
    downloadFile(csv, 'query_results.csv', 'text/csv;charset=utf-8;');
  };

  const handleExportJson = () => {
    const json = JSON.stringify(sortedRows, null, 2);
    downloadFile(json, 'query_results.json', 'application/json');
  };

  return (
    <Paper
      elevation={2}
      sx={{
        height: collapsed || trackerOnly ? RESULTS_MIN_HEIGHT : panelHeight,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderColor: 'divider',
        transition: isDragging.current ? 'none' : 'height 0.2s ease',
        ...qbResultsDock,
      }}
      data-testid="qb-results-dock"
    >
      {/* Drag handle */}
      <Box
        onMouseDown={handleDragStart}
        sx={{
          height: 6,
          flexShrink: 0,
          cursor: 'row-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          '&:hover .drag-indicator': { opacity: 1 },
        }}
      >
        <DragHandleIcon
          className="drag-indicator"
          sx={{ fontSize: 16, color: 'text.disabled', opacity: 0.4, transition: 'opacity 0.15s' }}
        />
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          p: 0.75,
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: collapsed ? 0 : 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Tooltip title={collapsed ? 'Expand results' : 'Collapse results'}>
          <IconButton size="small" onClick={toggleCollapse} sx={{ p: 0.25 }}>
            {collapsed ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Typography variant="subtitle2" sx={{ mr: 1, whiteSpace: 'nowrap', ...qbResultsTitle }}>
          Results {displayResults ? `(${filterText.trim() ? `${sortedRows.length} of ` : ''}${displayResults.row_count} rows, limit: ${userSettings.resultLimit.toLocaleString()})` : ''}
        </Typography>
        {queryRunning && !error && <Box data-testid="qb-query-tracker"><QueryExecuteTracker /></Box>}

        {!collapsed && (
          <>
            {displayResults && (
              <TextField
                size="small"
                placeholder="Filter results..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ flex: 1, maxWidth: 280, '& input': { fontSize: 12, py: 0.5 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  ...(filterText
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setFilterText('')} sx={{ p: 0.25 }}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : {}),
                }}
              />
            )}

            <Box sx={{ flex: 1 }} />

            {displayResults && (
              <>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton size="small" onClick={handleCopyClipboard}>
                    {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export CSV">
                  <IconButton size="small" onClick={handleExportCsv}>
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export JSON">
                  <IconButton size="small" onClick={handleExportJson}>
                    <DataObjectIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {displaySQL && (
              <Tooltip title="View SQL">
                <IconButton size="small" onClick={() => setShowSQL(true)}>
                  <CodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Pin current results">
              <span>
                <IconButton
                  size="small"
                  onClick={pinCurrentResults}
                  disabled={!queryResults || activeResultTab !== null}
                  aria-label="Pin current results"
                >
                  <PushPinIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      {/* SQL Preview Dialog */}
      <Dialog open={showSQL && !!displaySQL} onClose={() => setShowSQL(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon fontSize="small" />
            Generated SQL
          </Box>
          <IconButton size="small" onClick={() => setShowSQL(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {displaySQL && <SqlPreview sql={displaySQL} maxHeight={400} />}
        </DialogContent>
      </Dialog>

      {/* Tab bar for pinned results */}
      {pinnedResults.length > 0 && !collapsed && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            px: 1,
            borderBottom: 1,
            borderColor: 'divider',
            overflowX: 'auto',
            flexShrink: 0,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 2 },
          }}
        >
          <Box
            onClick={() => setActiveResultTab(null)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              ...qbResultTab(activeResultTab === null),
            }}
          >
            Current
            {queryResults && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  ml: 0.5,
                }}
              />
            )}
          </Box>

          {pinnedResults.map((pin) => {
            const isActive = activeResultTab === pin.id;
            const isEditing = editingTabId === pin.id;

            return (
              <Box
                key={pin.id}
                onClick={() => setActiveResultTab(pin.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  ...qbResultTab(isActive),
                }}
              >
                <PushPinIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                {isEditing ? (
                  <TextField
                    size="small"
                    value={editingTabLabel}
                    onChange={(e) => setEditingTabLabel(e.target.value)}
                    onBlur={() => {
                      if (editingTabLabel.trim()) renamePin(pin.id, editingTabLabel.trim());
                      setEditingTabId(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (editingTabLabel.trim()) renamePin(pin.id, editingTabLabel.trim());
                        setEditingTabId(null);
                      } else if (e.key === 'Escape') {
                        setEditingTabId(null);
                      }
                    }}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      width: 140,
                      '& input': { fontSize: 12, py: 0.25, px: 0.5 },
                    }}
                  />
                ) : (
                  <span
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditingTabId(pin.id);
                      setEditingTabLabel(pin.label);
                    }}
                  >
                    {pin.label}
                  </span>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    unpinResult(pin.id);
                  }}
                  sx={{ p: 0.25, ml: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pinned tab info line */}
      {activePin && !collapsed && (
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'action.hover', fontSize: 11, color: 'text.secondary', flexShrink: 0 }}>
          Pinned · {activePin.selectedMetrics.length} metrics · {activePin.selectedDimensions.length} dims
          {activePin.filterSummary && ` · ${activePin.filterSummary}`}
        </Box>
      )}

      {/* Error card — when last execute/generate failed */}
      {!collapsed && error && (
        <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
          <ResultErrorCard />
        </Box>
      )}

      {/* Empty state when on Current tab with no results */}
      {!collapsed && !error && !displayResults && pinnedResults.length > 0 && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No current results. Run a query or select a pinned tab.
          </Typography>
        </Box>
      )}

      {/* #1818: the reading line of the results on show (the current run's or a pinned tab's), above its numbers */}
      {!collapsed && !error && displayResults?.readings && (
        <Box sx={{ px: 2 }}><ReadingNote readings={displayResults.readings} dense /></Box>
      )}

      {/* Table */}
      {!collapsed && !error && displayResults && (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {columns.map((col, i) => {
                    const isSorted = sortColumn === col;
                    return (
                      <th
                        key={i}
                        onClick={() => handleSortClick(col)}
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          backgroundColor: isSorted ? 'var(--agent-tint)' : 'var(--surface)',
                          fontWeight: 700,
                          fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink-3)', borderBottom: '1px solid var(--line-soft)',
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {col}
                          {isSorted && sortDirection === 'asc' && (
                            <ArrowUpwardIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                          )}
                          {isSorted && sortDirection === 'desc' && (
                            <ArrowDownwardIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ padding: '16px 12px', textAlign: 'center', color: 'var(--ink-3)', fontStyle: 'italic' }}
                    >
                      No rows match the filter.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, rowIdx) => (
                    <tr key={rowIdx} style={{ backgroundColor: rowIdx % 2 === 0 ? 'var(--surface)' : 'var(--surface2)' }}>
                      {columns.map((col, cellIdx) => {
                        const cell = (row as any)[col];
                        const cellStr = cell === null || cell === undefined ? null : String(cell);
                        const isMatch =
                          filterText.trim() &&
                          cellStr !== null &&
                          cellStr.toLowerCase().includes(filterText.toLowerCase());
                        return (
                          <td
                            key={cellIdx}
                            style={{
                              padding: '6px 12px',
                              borderBottom: '1px solid var(--sunk)',
                              whiteSpace: 'nowrap',
                              backgroundColor: isMatch ? 'var(--warn-tint)' : undefined,
                            }}
                          >
                            {cellStr === null ? (
                              <span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>null</span>
                            ) : (
                              cellStr
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination bar */}
            {totalFilteredRows > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'var(--surface2)',
                  position: 'sticky',
                  bottom: 0,
                  flexShrink: 0,
                  fontSize: 12,
                }}
              >
                <Typography variant="caption" sx={{ mr: 0.5 }}>
                  Rows per page:
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                  sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.25, px: 1 } }}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption">
                  {totalFilteredRows === 0
                    ? '0 of 0'
                    : `${safePage * rowsPerPage + 1}–${Math.min((safePage + 1) * rowsPerPage, totalFilteredRows)} of ${totalFilteredRows}`}
                </Typography>
                <IconButton
                  size="small"
                  disabled={safePage === 0}
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  sx={{ p: 0.25 }}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={safePage >= totalPages - 1}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                  sx={{ p: 0.25 }}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default BottomResults;
